/**
 * Utility functions for extracting inventory items from form payloads
 * and converting them to Offer/Invoice positions
 */
import {
    alignTableRows,
    IAlignmentReport,
    parseTableRows,
    toAlignedColumns,
} from "./tableRowAligner";

export interface IExtractedInventoryItem {
    id?: number | string; // Database ID (optional)
    shortName: string;
    name: string; // Full name of the inventory item
    description: string; // Description of the inventory item
    salesPrice: number; // Sales price in cents or main currency unit
    count: number;
    totalQty: number;
    sources: Array<{
        formIndex: number;
        fieldType: string;
        fieldHashCode: string;
        tableRowIndex?: number;
    }>;
    tableRowHints: string[]; // Hints like "Tür 1", "Tür 2" for table items
    formType?: string;
}

export interface IFormPayload {
    fields?: IFormField[];
    entryName?: string;
    form?: {
        name?: string;
        endpoint?: string;
    };
}

export interface IFormField {
    type: string;
    name?: string;
    hashCode?: string;
    label?: string;
    subfields?: IFormField[];
    value?: any;
    initialValue?: any;
    [key: string]: any;
}

export interface IInvoicePosition {
    nr: number;
    title: string;
    description: string;
    qty: number;
    unit: "repair" | "service" | "disposal" | "hours";
    price: number;
    inventory_item_id: number | null;
    in_stock: boolean;
    inventory_item_missing?: boolean;
    userModified?: boolean;
}

export interface ITableAlignment {
    tableLabel: string;
    report: IAlignmentReport;
}

export interface IExtractedItemsResult {
    items: IExtractedInventoryItem[];
    totalCount: number;
    formLocations?: string[];
    formEntryNames?: string[];
    formProtocolLabels?: string[];
    formTypeOrder?: string[];
    alignments: ITableAlignment[];
}

export function isTurListeFormName(formName?: string): boolean {
    const name = (formName || "").toLowerCase();
    return name.indexOf("türliste") !== -1 || name.indexOf("tuerliste") !== -1;
}

/**
 * Resolves the schema fields from a form_revision.form_data value.
 * Handles both the flat shape ({"fields":[...]}, optionally JSON-encoded)
 * and the legacy shape where a serialized form object nests the fields
 * under latest_revision.form_data (itself a JSON string).
 */
export function parseSchemaFields(rawFormData: any): IFormField[] {
    let data: any = rawFormData;
    for (let depth = 0; depth < 6 && data; depth++) {
        if (typeof data === "string") {
            try {
                data = JSON.parse(data);
            } catch (e) {
                return [];
            }
            continue;
        }
        if (typeof data !== "object") {
            return [];
        }
        if (Array.isArray(data.fields)) {
            return data.fields;
        }
        data = data.latest_revision && data.latest_revision.form_data;
    }
    return [];
}

/**
 * Recursively finds all fields of specific types in a form field tree
 */
function findFieldsByType(
    fields: IFormField[],
    targetTypes: string[],
    parentPath: string = "",
    results: Array<{ field: IFormField; path: string }> = []
): Array<{ field: IFormField; path: string }> {
    for (const field of fields) {
        const currentPath = parentPath
            ? `${parentPath}.${field.hashCode || field.name || "unknown"}`
            : field.hashCode || field.name || "unknown";

        if (targetTypes.includes(field.type)) {
            results.push({ field, path: currentPath });
        }

        // Recursively search in subfields (GroupField, TableField, etc.)
        if (field.subfields && Array.isArray(field.subfields)) {
            findFieldsByType(field.subfields, targetTypes, currentPath, results);
        }
    }

    return results;
}

/**
 * Parses inventory item values from a field
 * Supports both array format and string format (comma-separated)
 */
function parseInventoryItemValues(value: any): string[] {
    if (!value) {
        return [];
    }

    if (Array.isArray(value)) {
        return value.filter((v) => typeof v === "string" && v.trim() !== "");
    }

    if (typeof value === "string") {
        return value
            .split(",")
            .map((v) => v.trim())
            .filter((v) => v !== "");
    }

    return [];
}

/**
 * Parses table data from a TableField
 * Table data is stored as a 2D array where each row contains column objects
 */
function parseTableData(tableField: IFormField): any[][] {
    return parseTableRows(tableField.value) || [];
}

/**
 * Extracts inventory items from InventoryItemSelectTableRow within a TableField
 * Returns array of { shortName, name, description, salesPrice, rowIndex, columnValue }
 */
function extractItemsFromTable(
    tableField: IFormField,
    formIndex: number,
    alignments: ITableAlignment[]
): IExtractedInventoryItem[] {
    const items: IExtractedInventoryItem[] = [];
    const rawTableData = parseTableData(tableField);

    if (!rawTableData || !Array.isArray(rawTableData)) {
        return items;
    }

    // Find the column index for InventoryItemSelectTableRow
    const subfields = tableField.subfields || [];
    const inventoryItemColumnIndex = subfields.findIndex(
        (subfield) => subfield.type === "InventoryItemSelectTableRow"
    );

    if (inventoryItemColumnIndex === -1) {
        return items;
    }

    const { rows: tableData, report } = alignTableRows(rawTableData, toAlignedColumns(subfields));

    // Iterate through table rows
    tableData.forEach((row, rowIndex) => {
        if (!Array.isArray(row)) {
            return;
        }

        const columnData = row[inventoryItemColumnIndex];
        if (!columnData) {
            return;
        }

        // Column data can have value as array of {label, value, cell} objects or single value
        // The full inventory item data is in the .cell property
        const rawValues = columnData.value;
        const values = Array.isArray(rawValues) ? rawValues : rawValues ? [rawValues] : [];

        values.forEach((val: any) => {
            if (!val) {
                return;
            }
            if (typeof val === "string") {
                items.push({
                    id: null,
                    shortName: val,
                    name: val,
                    description: "",
                    salesPrice: 0,
                    count: 1,
                    totalQty: 1,
                    sources: [
                        {
                            formIndex,
                            fieldType: "InventoryItemSelectTableRow",
                            fieldHashCode: tableField.hashCode || "",
                            tableRowIndex: rowIndex,
                        },
                    ],
                    tableRowHints: [`Tür ${rowIndex + 1}`],
                });
                return;
            }

            const cellData = val?.cell || val;
            const shortName = cellData?.short_name || cellData?.value || cellData?.label || "";
            const name = cellData?.name || shortName;
            const description = cellData?.description || "";
            const salesPrice = typeof cellData?.sales_price === "number" ? cellData.sales_price : 0;

            if (shortName) {
                items.push({
                    id: cellData?.id ?? null,
                    shortName,
                    name,
                    description,
                    salesPrice,
                    count: 1,
                    totalQty: 1,
                    sources: [
                        {
                            formIndex,
                            fieldType: "InventoryItemSelectTableRow",
                            fieldHashCode: tableField.hashCode || "",
                            tableRowIndex: rowIndex,
                        },
                    ],
                    tableRowHints: [`Tür ${rowIndex + 1}`],
                });
            }
        });
    });

    if (report.rowsShifted > 0 || report.unmatchedCells > 0) {
        alignments.push({
            tableLabel: tableField.label || tableField.hashCode || "",
            report,
        });
    }

    return items;
}

function findFieldByHashCode(fields: IFormField[], hashCode: string): IFormField | undefined {
    for (const field of fields) {
        if (field.hashCode === hashCode) {
            return field;
        }
        if (field.subfields && Array.isArray(field.subfields)) {
            const found = findFieldByHashCode(field.subfields, hashCode);
            if (found) {
                return found;
            }
        }
    }
    return undefined;
}

function isBlankValueString(value: string): boolean {
    return value === "" || value === "null" || value === "undefined" || value === "NaN";
}

function entryToLabel(entry: any, items?: any[]): string {
    if (entry == null) {
        return "";
    }
    if (typeof entry === "object") {
        if (entry.label != null && String(entry.label).trim() !== "") {
            return String(entry.label).trim();
        }
        const id = entry.value != null ? entry.value : entry.id;
        if (id != null) {
            const item = Array.isArray(items)
                ? items.find((it: any) => it.id === id || it.name === id)
                : undefined;
            return item ? String(item.name) : String(id);
        }
        return "";
    }
    const match = Array.isArray(items)
        ? items.find((it: any) => it.id === entry || it.name === entry)
        : undefined;
    return match ? String(match.name) : String(entry);
}

function resolveFieldDisplayValue(field?: IFormField): string {
    if (!field) {
        return "";
    }
    let value: any = field.value != null ? field.value : field.initialValue;
    if (value == null) {
        return "";
    }

    if (typeof value === "string") {
        const trimmed = value.trim();
        if (isBlankValueString(trimmed)) {
            return "";
        }
        if (trimmed[0] === "[" || trimmed[0] === "{") {
            try {
                value = JSON.parse(trimmed);
            } catch (e) {
                return trimmed;
            }
        } else {
            value = trimmed;
        }
    }

    if (value == null) {
        return "";
    }

    const entries = Array.isArray(value) ? value : [value];
    return entries
        .map((entry) => entryToLabel(entry, field.items))
        .filter((name) => name && !isBlankValueString(name.trim()))
        .join(", ");
}

function getProtocolLocationLabel(payload: IFormPayload): string {
    const fields = payload.fields || [];
    const stockwerk = resolveFieldDisplayValue(findFieldByHashCode(fields, "stockwerk"));
    const standort = resolveFieldDisplayValue(findFieldByHashCode(fields, "entity_location"));
    return [stockwerk, standort]
        .filter((s) => s && s.trim() !== "")
        .join(" ")
        .trim();
}

export function formTypeLabel(formName?: string): string {
    return (formName || "").replace(/^\d+_/, "").trim();
}

function getProtocolShortLabel(payload: IFormPayload): string {
    const fields = payload.fields || [];
    const rpnummer = resolveFieldDisplayValue(findFieldByHashCode(fields, "rpnummer"));
    return [
        rpnummer ? `RP Nr. ${rpnummer}` : "",
        formTypeLabel(payload.form && payload.form.name),
        getProtocolLocationLabel(payload),
    ]
        .filter((s) => s && s.trim() !== "")
        .join(" ")
        .trim();
}

/**
 * Extracts inventory items from multiple form payloads
 * Aggregates items by short_name per form type (Türliste groups first,
 * remaining types in order of first appearance)
 *
 * @param formPayloads - Array of form payload objects
 * @returns IExtractedItemsResult containing aggregated items and total count
 */
export function extractInventoryItemsFromForms(
    formPayloads: IFormPayload[]
): IExtractedItemsResult {
    const groups = new Map<
        string,
        {
            formType: string;
            isTurListe: boolean;
            items: Map<string, IExtractedInventoryItem>;
        }
    >();
    const formLocations: string[] = [];
    const formEntryNames: string[] = [];
    const formProtocolLabels: string[] = [];
    const alignments: ITableAlignment[] = [];

    formPayloads.forEach((payload, formIndex) => {
        const fields = payload.fields || [];
        formLocations[formIndex] = getProtocolLocationLabel(payload);
        formEntryNames[formIndex] = payload.entryName || "";

        const formType = (payload.form && payload.form.name) || "";
        formProtocolLabels[formIndex] = isTurListeFormName(formType)
            ? ""
            : getProtocolShortLabel(payload);
        if (!groups.has(formType)) {
            groups.set(formType, {
                formType,
                isTurListe: isTurListeFormName(formType),
                items: new Map<string, IExtractedInventoryItem>(),
            });
        }
        const itemMap = groups.get(formType)!.items;

        // Find all InventoryItemSelectField fields
        const selectFields = findFieldsByType(fields, ["InventoryItemSelectField"]);

        selectFields.forEach(({ field }) => {
            const rawValue = field.value;
            let values: any[] = [];

            if (typeof rawValue === "string") {
                try {
                    const parsed = JSON.parse(rawValue);
                    if (Array.isArray(parsed)) {
                        values = parsed;
                    } else {
                        values = rawValue
                            .split(",")
                            .map((v) => v.trim())
                            .filter((v) => v);
                    }
                } catch (e) {
                    values = rawValue
                        .split(",")
                        .map((v) => v.trim())
                        .filter((v) => v);
                }
            } else if (Array.isArray(rawValue)) {
                values = rawValue;
            }

            values.forEach((val: any) => {
                const cellData = val?.cell || val;
                const shortName =
                    typeof val === "string"
                        ? val
                        : cellData?.short_name ||
                          cellData?.value ||
                          cellData?.name ||
                          cellData?.label ||
                          "";
                const name =
                    typeof val === "object"
                        ? cellData?.name || cellData?.label || shortName
                        : shortName;
                // const description = typeof val === "object" ? cellData?.description || "" : "";
                const salesPrice =
                    typeof cellData?.sales_price === "number"
                        ? cellData.sales_price
                        : typeof cellData?.salesPrice === "number"
                        ? cellData.salesPrice
                        : 0;

                if (!shortName) return;

                if (!itemMap.has(shortName)) {
                    itemMap.set(shortName, {
                        id: typeof cellData?.id === "number" ? cellData.id : null,
                        shortName,
                        name,
                        description: "",
                        salesPrice,
                        count: 0,
                        totalQty: 0,
                        sources: [],
                        tableRowHints: [],
                        formType,
                    });
                }

                const item = itemMap.get(shortName)!;
                if (!item.id && typeof cellData?.id === "number") {
                    item.id = cellData.id;
                }
                item.count += 1;
                item.totalQty += 1;
                item.sources.push({
                    formIndex,
                    fieldType: field.type,
                    fieldHashCode: field.hashCode || "",
                });
            });
        });

        // Find all TableField fields that contain InventoryItemSelectTableRow
        const tableFields = findFieldsByType(fields, ["TableField"]);

        tableFields.forEach(({ field }) => {
            const tableItems = extractItemsFromTable(field, formIndex, alignments);

            tableItems.forEach((tableItem) => {
                const {
                    id,
                    shortName,
                    name,
                    description,
                    salesPrice,
                    sources,
                    tableRowHints,
                    count,
                    totalQty,
                } = tableItem;
                if (!itemMap.has(shortName)) {
                    itemMap.set(shortName, {
                        id,
                        shortName,
                        name,
                        description,
                        salesPrice,
                        count: 0,
                        totalQty: 0,
                        sources: [],
                        tableRowHints: [],
                        formType,
                    });
                }

                const item = itemMap.get(shortName)!;
                // Set id if not already set and present
                if (!item.id && id) item.id = id;
                item.count += count || 1;
                item.totalQty += totalQty || 1;
                // Update with first available full data if not already set
                if (!item.name && name) item.name = name;
                if (!item.description && description) item.description = description;
                if (!item.salesPrice && salesPrice) item.salesPrice = salesPrice;
                if (sources && sources.length > 0) {
                    item.sources.push(...sources);
                }
                if (tableRowHints && tableRowHints.length > 0) {
                    item.tableRowHints.push(...tableRowHints);
                }
            });
        });
    });

    const groupKeys = Array.from(groups.keys()).filter((key) => groups.get(key)!.items.size > 0);
    const orderedKeys = groupKeys
        .filter((key) => groups.get(key)!.isTurListe)
        .concat(groupKeys.filter((key) => !groups.get(key)!.isTurListe));

    const items = orderedKeys.reduce(
        (acc: IExtractedInventoryItem[], key) =>
            acc.concat(Array.from(groups.get(key)!.items.values())),
        []
    );
    const totalCount = items.reduce((sum, item) => sum + item.count, 0);

    return {
        items,
        totalCount,
        formLocations,
        formEntryNames,
        formProtocolLabels,
        formTypeOrder: orderedKeys,
        alignments,
    };
}

/**
 * Converts extracted inventory items to InvoicePositionsListField data format
 * suitable for creating an Offer form
 *
 * @param extractedItems - Result from extractInventoryItemsFromForms
 * @param options - Optional configuration for the conversion
 * @returns Object compatible with InvoicePositionsListField data structure
 */
export function convertToOfferPositions(
    extractedItems: IExtractedItemsResult,
    options: {
        defaultUnit?: "repair" | "service" | "disposal" | "hours";
        defaultPrice?: number;
        includeHintsInDescription?: boolean;
    } = {}
): {
    invoicePositions: IInvoicePosition[];
    discount: number;
    vat: { value: number; label: string };
    shippingInCents: number;
    notice: string;
    notice2: string;
    processDateBegin: string | null;
    processDateEnd: string | null;
    processDateDelimiter: string;
    processWorkers: any[];
    unitSelection: { label: string; value: string };
} {
    const { defaultUnit = "repair", defaultPrice = 0, includeHintsInDescription = true } = options;

    const defaultVat = {
        value: 0.19,
        label: "19 % Mwst.",
    };

    const unitOptions = [
        { label: "Reparatur", value: "repair" },
        { label: "Service", value: "service" },
        { label: "Verkauf", value: "disposal" },
    ];

    const formLocations = extractedItems.formLocations || [];
    const formEntryNames = extractedItems.formEntryNames || [];
    const formProtocolLabels = extractedItems.formProtocolLabels || [];
    const labelForForm = (formIndex: number): string =>
        formProtocolLabels[formIndex] ||
        formLocations[formIndex] ||
        formEntryNames[formIndex] ||
        "";

    // Create invoice positions from extracted items
    const invoicePositions: IInvoicePosition[] = extractedItems.items.map((item, index) => {
        let description = item.description || "";

        if (includeHintsInDescription) {
            const doorsByForm = new Map<number, Set<number>>();
            for (const source of item.sources) {
                if (source.tableRowIndex == null) {
                    continue;
                }
                if (!doorsByForm.has(source.formIndex)) {
                    doorsByForm.set(source.formIndex, new Set<number>());
                }
                doorsByForm.get(source.formIndex)!.add(source.tableRowIndex + 1);
            }

            const labelCounts = new Map<string, number>();
            doorsByForm.forEach((doorNumbers, formIndex) => {
                const label = labelForForm(formIndex);
                labelCounts.set(label, (labelCounts.get(label) || 0) + 1);
            });

            const doorLines: string[] = [];
            doorsByForm.forEach((doorNumbers, formIndex) => {
                let label = labelForForm(formIndex);
                if ((labelCounts.get(label) || 0) > 1 && formEntryNames[formIndex]) {
                    label = formEntryNames[formIndex];
                }
                const doors = Array.from(doorNumbers)
                    .sort((a, b) => a - b)
                    .map((n) => `Tür ${n}`)
                    .join(", ");
                doorLines.push(label ? `${label}: ${doors}` : doors);
            });

            const protocolLines: string[] = [];
            for (const source of item.sources) {
                if (source.tableRowIndex != null) {
                    continue;
                }
                const label =
                    formProtocolLabels[source.formIndex] || formEntryNames[source.formIndex] || "";
                if (label === "" || protocolLines.indexOf(label) !== -1) {
                    continue;
                }
                protocolLines.push(label);
            }

            const lines = doorLines.concat(protocolLines);
            if (lines.length > 0) {
                description = lines.join("\n");
            }
        }

        // Use extracted sales price if available, otherwise use default
        const price = item.salesPrice > 0 ? item.salesPrice : defaultPrice;

        return {
            nr: index + 1,
            title: item.name || item.shortName,
            description,
            qty: item.totalQty,
            unit: defaultUnit,
            price,
            inventory_item_id:
                typeof item.id === "number"
                    ? item.id
                    : typeof item.id === "string" && /^\d+$/.test(item.id)
                    ? parseInt(item.id, 10)
                    : null,
            in_stock: true,
        };
    });

    return {
        invoicePositions,
        discount: 0,
        vat: defaultVat,
        shippingInCents: 0,
        notice: "",
        notice2: "",
        processDateBegin: null,
        processDateEnd: null,
        processDateDelimiter: "bis",
        processWorkers: [],
        unitSelection: unitOptions[0],
    };
}

/**
 * Complete workflow: Extract items from forms and create Offer data
 * This is a convenience function that combines extraction and conversion
 *
 * @param formPayloads - Array of form payloads to process
 * @param options - Optional configuration for Offer creation
 * @returns Object with extraction results and offer data
 */
export function createOfferFromForms(
    formPayloads: IFormPayload[],
    options?: Parameters<typeof convertToOfferPositions>[1]
): {
    extraction: IExtractedItemsResult;
    offerData: ReturnType<typeof convertToOfferPositions>;
} {
    const extraction = extractInventoryItemsFromForms(formPayloads);
    const offerData = convertToOfferPositions(extraction, options);

    return {
        extraction,
        offerData,
    };
}
